import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '@festpix/services';
import { getUserWallet } from '@festpix/services';
import { createCheckoutProPreference, getPaymentStatus } from '../../services/payment';
import { colors, spacing, borderRadius, typography, shadows } from '@festpix/ui';
import AttendeeLayout from '../../components/AttendeeLayout';

const PAYMENT_ENABLED = true;
const USE_SANDBOX = import.meta.env.DEV;

interface CheckoutModalProps {
  visible: boolean;
  checkoutUrl: string;
  transactionId: string;
  onClose: () => void;
  onPaymentComplete: (status: 'success' | 'failure' | 'pending') => void;
}

function CheckoutModal({
  visible,
  checkoutUrl,
  transactionId,
  onClose,
  onPaymentComplete
}: CheckoutModalProps) {
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (visible && transactionId) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const status = await getPaymentStatus(transactionId);
          if (status.status === 'approved') {
            clearInterval(pollIntervalRef.current!);
            onPaymentComplete('success');
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            clearInterval(pollIntervalRef.current!);
            onPaymentComplete('failure');
          }
        } catch (error) {
          console.log('Error polling payment status:', error);
        }
      }, 3000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [visible, transactionId, onPaymentComplete]);

  if (!visible) return null;

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContainer}>
        <div style={styles.modalHeader}>
          <button onClick={onClose} style={styles.closeButton}>
            Cancelar
          </button>
          <span style={styles.modalTitle}>Pagamento</span>
          <div style={{ width: 80 }} />
        </div>

        <iframe
          src={checkoutUrl}
          style={styles.iframe}
          title="Checkout Mercado Pago"
        />
      </div>
    </div>
  );
}

export default function RechargeScreen() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [walletId, setWalletId] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [checkoutVisible, setCheckoutVisible] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [currentTransactionId, setCurrentTransactionId] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [rechargedAmount, setRechargedAmount] = useState(0);
  const [inputBlink, setInputBlink] = useState(false);

  useEffect(() => {
    loadWalletId();
  }, []);

  const loadWalletId = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const wallet = await getUserWallet(user.uid);
        if (wallet) {
          setWalletId(wallet.id);
        }
      }
    } catch (error) {
      console.error('Error loading wallet:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar carteira' });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleRecharge = async () => {
    const amountNum = parseFloat(amount.replace(',', '.'));

    if (isNaN(amountNum) || amountNum <= 0) {
      setMessage({ type: 'error', text: 'Por favor, insira um valor válido' });
      return;
    }

    if (amountNum < 1) {
      setMessage({ type: 'error', text: 'O valor mínimo é R$ 1,00' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const preference = await createCheckoutProPreference(walletId, amountNum);
      const url = USE_SANDBOX ? preference.sandboxInitPoint : preference.initPoint;

      setCheckoutUrl(url);
      setCurrentTransactionId(preference.transactionId);
      setCheckoutVisible(true);
    } catch (error) {
      console.error('Error creating preference:', error);
      setMessage({ type: 'error', text: 'Erro ao iniciar pagamento' });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentComplete = useCallback((status: 'success' | 'failure' | 'pending') => {
    setCheckoutVisible(false);
    setCheckoutUrl('');
    setCurrentTransactionId('');

    if (status === 'success') {
      const amountNum = parseFloat(amount.replace(',', '.'));
      setRechargedAmount(amountNum);
      setShowSuccessScreen(true);
      setAmount('');
    } else if (status === 'pending') {
      setMessage({ type: 'info', text: 'Seu pagamento está sendo processado. Você será notificado quando for aprovado.' });
      setAmount('');
    } else {
      setMessage({ type: 'error', text: 'O pagamento não foi concluído. Por favor, tente novamente.' });
    }
  }, [amount]);

  const handleCloseCheckout = () => {
    if (window.confirm('Deseja realmente cancelar o pagamento?')) {
      setCheckoutVisible(false);
      setCheckoutUrl('');
      setCurrentTransactionId('');
    }
  };

  const quickAmounts = [10, 20, 50, 100];

  const handleQuickAmountClick = (value: number) => {
    setAmount(value.toString());
    setInputBlink(true);
    setTimeout(() => setInputBlink(false), 300);
  };

  if (initialLoading) {
    return (
      <AttendeeLayout showBackButton>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Carregando...</p>
        </div>
      </AttendeeLayout>
    );
  }

  if (showSuccessScreen) {
    return (
      <AttendeeLayout>
        <div style={styles.successContainer}>
          <div style={styles.successIcon}>
            <span style={styles.successEmoji}>✓</span>
          </div>
          <h1 style={styles.successTitle}>Recarga Realizada!</h1>
          <p style={styles.successMessage}>
            Sua carteira foi recarregada com sucesso.
          </p>

          <div style={styles.successCard}>
            <div style={styles.successRow}>
              <span style={styles.successLabel}>Valor adicionado:</span>
              <span style={styles.successValue}>
                R$ {rechargedAmount.toFixed(2).replace('.', ',')}
              </span>
            </div>
          </div>

          <button
            style={styles.successButton}
            onClick={() => navigate('/')}
          >
            Voltar para Início
          </button>
        </div>
      </AttendeeLayout>
    );
  }

  return (
    <AttendeeLayout showBackButton>
      <div style={styles.innerContainer}>
        <h1 style={styles.title}>Recarregar Carteira</h1>
      <p style={styles.subtitle}>
        Adicione créditos à sua carteira digital usando PIX, cartão de crédito ou débito.
      </p>

      <div style={styles.quickAmountsContainer}>
        <p style={styles.quickAmountsLabel}>Valores rápidos:</p>
        <div style={styles.quickAmountsRow}>
          {quickAmounts.map((value) => (
            <button
              key={value}
              style={styles.quickAmountButton}
              onClick={() => handleQuickAmountClick(value)}
            >
              R$ {value}
            </button>
          ))}
        </div>
      </div>

      <p style={styles.inputLabel}>Ou digite outro valor:</p>
      <div style={{
        ...styles.inputContainer,
        ...(inputBlink ? styles.inputBlink : {})
      }}>
        <span style={styles.currencyPrefix}>R$</span>
        <input
          style={styles.input}
          placeholder="0,00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          type="text"
          inputMode="decimal"
        />
      </div>

      {message && (
        <p style={{
          ...styles.messageText,
          color: message.type === 'success' ? colors.success :
                 message.type === 'error' ? colors.error : colors.info
        }}>
          {message.text}
        </p>
      )}

      <button
        style={{
          ...styles.button,
          ...(!PAYMENT_ENABLED || loading || !amount ? styles.buttonDisabled : {})
        }}
        onClick={handleRecharge}
        disabled={loading || !PAYMENT_ENABLED || !amount}
      >
        {loading ? 'Processando...' : 'Continuar para Pagamento'}
      </button>

      {!PAYMENT_ENABLED && (
        <p style={styles.disabledMessage}>Pagamentos em breve disponíveis</p>
      )}

      <div style={styles.noteContainer}>
        <p style={styles.noteText}>
          A recarga com Dinheiro, Fichas, PIX ou Cartão também pode ser feita nos caixas
        </p>
      </div>

      <div style={styles.infoContainer}>
        <p style={styles.infoTitle}>Formas de pagamento aceitas:</p>
        <p style={styles.infoItem}>• PIX (aprovação instantânea)</p>
        <p style={styles.infoItem}>• Cartão de Crédito</p>
        <p style={styles.infoItem}>• Cartão de Débito</p>
        <p style={styles.infoItem}>• Saldo Mercado Pago</p>
      </div>

        <CheckoutModal
          visible={checkoutVisible}
          checkoutUrl={checkoutUrl}
          transactionId={currentTransactionId}
          onClose={handleCloseCheckout}
          onPaymentComplete={handlePaymentComplete}
        />
      </div>
    </AttendeeLayout>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  innerContainer: {
    paddingBottom: spacing.xl,
  },
  container: {
    minHeight: '100vh',
    padding: spacing.lg,
    backgroundColor: colors.background,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: colors.background,
  },
  spinner: {
    width: 40,
    height: 40,
    border: `3px solid ${colors.border}`,
    borderTop: `3px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: spacing.md,
    color: colors.textSecondary,
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: colors.primary,
    fontSize: typography.fontSize.md,
    cursor: 'pointer',
    padding: 0,
    marginBottom: spacing.md,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.xs,
    textAlign: 'center',
    color: colors.text,
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  quickAmountsContainer: {
    marginBottom: spacing.lg,
  },
  quickAmountsLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  quickAmountsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.xs,
  },
  quickAmountButton: {
    flex: 1,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    textAlign: 'center',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.primary,
    cursor: 'pointer',
  },
  inputLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    marginBottom: spacing.lg,
    transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
  },
  inputBlink: {
    backgroundColor: colors.primaryLight || '#e8f5e9',
    borderColor: colors.primary,
  },
  currencyPrefix: {
    fontSize: typography.fontSize.lg,
    color: colors.textSecondary,
    paddingLeft: spacing.md,
  },
  input: {
    flex: 1,
    padding: spacing.md,
    fontSize: typography.fontSize.lg,
    color: colors.text,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
  },
  button: {
    backgroundColor: colors.primary,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    color: colors.textLight,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    width: '100%',
    marginBottom: spacing.md,
    cursor: 'pointer',
    border: 'none',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  disabledMessage: {
    textAlign: 'center',
    color: colors.textSecondary,
    fontSize: typography.fontSize.sm,
    marginTop: spacing.sm,
    fontStyle: 'italic',
  },
  messageText: {
    textAlign: 'center',
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.md,
  },
  noteContainer: {
    marginTop: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.info + '15',
    borderRadius: borderRadius.md,
    borderLeft: `4px solid ${colors.info}`,
  },
  noteText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
    margin: 0,
    fontWeight: typography.fontWeight.medium,
  },
  infoContainer: {
    marginTop: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: borderRadius.md,
  },
  infoTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  infoItem: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
    margin: 0,
    marginTop: spacing.xs,
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContainer: {
    width: '100%',
    height: '100%',
    maxWidth: 500,
    maxHeight: '90vh',
    backgroundColor: colors.background,
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
  },
  modalTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  closeButton: {
    width: 80,
    color: colors.primary,
    fontSize: typography.fontSize.md,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  iframe: {
    flex: 1,
    width: '100%',
    border: 'none',
  },
  // Success Screen styles
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '70vh',
    padding: spacing.lg,
  },
  successIcon: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: colors.success,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.lg,
  },
  successEmoji: {
    fontSize: 50,
    color: colors.textLight,
  },
  successTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.sm,
    margin: 0,
  },
  successMessage: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
    margin: 0,
    marginTop: spacing.sm,
  },
  successCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    width: '100%',
    maxWidth: 360,
    marginBottom: spacing.xl,
    boxShadow: shadows.md,
  },
  successRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  successLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  successValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.success,
  },
  successButton: {
    backgroundColor: colors.primary,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    color: colors.textLight,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    width: '100%',
    maxWidth: 360,
    border: 'none',
    cursor: 'pointer',
    boxShadow: shadows.colored,
  },
};
